import * as Yup from 'yup';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Container,
  Stack,
  Typography,
  Button,
  useTheme,
  Collapse,
  Slide,
  Divider,
  Fade,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip
} from '@mui/material';
import React from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { alpha, styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import questionMarkCircleFill from '@iconify/icons-eva/question-mark-circle-fill';
import { TransitionGroup } from 'react-transition-group';
import { LoadingButton } from '@mui/lab';
import _ from 'lodash';
import toast from 'react-hot-toast';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import jwt from 'jsonwebtoken';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import 'moment/min/locales';

import * as feedbackService from '../../../services/feedback';
import QuestionsGenerator from '../../QuestionsGenerator';
import isEligible from '../../../utils/roleEligibleGetter';
import AddAttachment from './AddAttachment';
import { getAPIURL } from '../../../utils/envGetter';
import findIndex from '../../../utils/indexFinder';

ServicesDetailFeedbackQuestions.propTypes = {
  layanan: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  setQuestions: PropTypes.func.isRequired,
  selectedRequirement: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  informations: PropTypes.isRequired,
  toggleUpdate: PropTypes.any,
  setToggleUpdate: PropTypes.func.isRequired
};

function ServicesDetailFeedbackQuestions({
  layanan,
  questions,
  setQuestions,
  selectedRequirement,
  informations,
  user,
  toggleUpdate,
  setToggleUpdate
}) {
  const questionsID = {};
  const questionsSchema = {};
  const valuesFromAPI = layanan?.meta?.feedback?.metadata?.answers;

  const [modeTandaTangan, setModeTandaTangan] = React.useState('tte');
  const [dataDukungs, setDataDukungs] = React.useState({});

  questions.forEach((question) => {
    questionsID[question.id] = '';
    if (!_.isEmpty(valuesFromAPI)) questionsID[question.id] = valuesFromAPI[question.id];

    if (question.required) {
      if (question.type === 'textarray') {
        questionsSchema[question.id] = Yup.array().required(`${question.label} wajib di-isi`);
      } else {
        questionsSchema[question.id] = Yup.string().required(`${question.label} wajib di-isi`);
      }
    }
  });

  const QuestionSchema = Yup.object().shape(questionsSchema);
  const formik = useFormik({
    initialValues: {
      ...questionsID
    },
    enableReinitialize: true,
    validationSchema: QuestionSchema,
    onSubmit: () => {
      saveData();
    }
  });

  const [isSaved, setIsSaved] = React.useState(false);
  const [isSigned, setIsSigned] = React.useState(false);
  const [isVerified, setIsVerified] = React.useState(false);

  const saveData = async () => {
    try {
      // return alert(layanan?.attributes?.id_feedback);
      const body = {
        data: {
          type: 'feedback',
          id: layanan?.attributes?.id_feedback,
          attributes: {
            document_path: null,
            is_signed: null,
            status: values.status,
            creator: user.attributes.username,
            id_layanan: layanan?.id
          },
          meta: { ...layanan?.meta?.feedback?.metadata, answers: values }
        }
      };

      toast
        .promise(feedbackService.upsertFeedback(body), {
          loading: 'Menyimpan data ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Data berhasil disimpan</b>
        })
        .then(() => {
          setIsSaved(true);
          setToggleUpdate(!toggleUpdate);
        });
    } catch (error) {
      toast.error(error.message);
    }

    setSubmitting(false);
  };

  React.useEffect(() => {
    if (!_.isEmpty(layanan?.meta?.feedback?.metadata?.answers)) setIsSaved(true);
    if (layanan?.meta?.feedback?.is_signed && layanan?.meta?.feedback?.document_path)
      setIsSigned(true);
  }, []);

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    setFieldValue,
    setValues
  } = formik;

  const theme = useTheme();
  const navigate = useNavigate();

  const previewDocument = () => {
    const accessToken = jwt.sign(
      {
        requestor: layanan?.attributes?.requestor,
        id_requirement: selectedRequirement.id,
        modeTandaTangan,
        selectedRequirement
      },
      process.env.REACT_APP_SECRET_KEY,
      {
        expiresIn: '24h'
      }
    );

    navigate(`/pdf/${layanan.id}/${accessToken}`);
  };

  const openDocument = () => {
    const origin = getAPIURL();
    const path = `${origin}/pdf?path=${layanan.meta.feedback.document_path
      .replace('.', '')
      .substring(1)}&id=${layanan.meta.feedback.id}&filename=${getFormTitle()}`;
    window.open(path, '_blank');
  };

  const addTextArrayItem = (question) => {
    const newValues = { ...values };
    const currentArray = newValues[question.id] || [];
    const id = uuidv4();
    currentArray.push({ id, value: '' });
    newValues[question.id] = currentArray;
    setValues(newValues);
  };

  const removeTextArrayItem = (question, valueArray) => {
    const newArray = values[question.id].filter((item) => item.id !== valueArray.id);
    setValues({ ...values, [question.id]: newArray });
  };

  const setTextArrayValue = (e, item, valueItem) => {
    const { value } = e.target;
    const currentArray = values[item.id];
    const arrayIndex = findIndex(values[item.id], valueItem.id, 'id');

    currentArray[arrayIndex].value = value;
    setValues({ ...values, [item.id]: currentArray });
  };

  const setDateValue = (item, data) => {
    // moment.locale('id');
    const date = data.$d;
    const value = moment(date).format('DD MMMM YYYY');
    setValues({ ...values, [item.id]: value });
  };

  const changeTextArrayPosition = (item, index, target) => {
    try {
      if (target === 'up' && index === 0) return;
      if (target === 'down' && index === values[item.id].length - 1) return;
      const data = [...values[item.id]];
      const currentArray = [...values[item.id]];

      const targetValue = target === 'down' ? 1 : -1;

      currentArray[index] = data[index + targetValue];
      currentArray[index + targetValue] = data[index];

      setValues({ ...values, [item.id]: currentArray });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getFormTitle = () => {
    let title = 'Formulir';

    if (selectedRequirement.attributes.question_id === 'feedback') title = 'Formulir Tindak Lanjut';
    if (selectedRequirement.attributes.question_id === 'rekomendasi')
      title = 'Formulir Status Permohonan';

    return title;
  };
  return (
    <Stack
      direction="column"
      spacing={2}
      divider={
        (isEligible('feedback_form') || isEligible('feedback_result')) && (
          <Divider orientation="horizontal" flexItem />
        )
      }
    >
      {/* FEEDBACAK STATUS */}
      <Box pt={3}>
        <Stack direction="column" spacing={2}>
          <Typography variant="h4">{selectedRequirement.attributes.label}</Typography>
          <Stack
            spacing={1}
            direction={{ xs: 'column', sm: 'row' }}
            justifyItems="flex-start"
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon
                icon={isSaved ? checkmarkCircleFill : closeCircleFill}
                color={isSaved ? theme.palette.primary.main : theme.palette.grey[400]}
              />
              <Typography>Simpan Data</Typography>
            </Stack>
            {![
              'feedback',
              'tte',
              'splp',
              'aduan',
              'videotron',
              'cloud',
              'backup',
              'vpn',
              'email'
            ].includes(selectedRequirement.attributes.question_id) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icon
                  icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                  color={isSigned ? theme.palette.primary.main : theme.palette.grey[400]}
                />
                <Typography>Tanda Tangan (TTE/Manual)</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
      {/* FEEDBACK FORM */}
      {isEligible('feedback_form') && (
        <Stack direction="column" spacing={2}>
          <Typography variant="h6">{getFormTitle()}</Typography>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack
                spacing={2}
                direction={{ sm: 'column', xs: 'column', md: 'column', lg: 'row' }}
                divider={<Divider orientation="vertical" flexItem />}
              >
                <Stack
                  spacing={3}
                  direction="column"
                  flex={6}
                  divider={<Divider orientation="horizontal" flexItem />}
                >
                  <Fade in="true">
                    <Stack direction="column" spacing={2}>
                      {!_.isEmpty(questions) &&
                        questions.map((item) => (
                          <QuestionsGenerator
                            item={item}
                            values={values}
                            setFieldValue={setFieldValue}
                            getFieldProps={getFieldProps}
                            errors={errors}
                            touched={touched}
                            isSubmitting={isSubmitting}
                            handleSubmit={handleSubmit}
                            setSubmitting={setSubmitting}
                            dataDukungs={dataDukungs}
                            setDataDukungs={setDataDukungs}
                            addTextArrayItem={addTextArrayItem}
                            removeTextArrayItem={removeTextArrayItem}
                            setTextArrayValue={setTextArrayValue}
                            changeTextArrayPosition={changeTextArrayPosition}
                            layananStatus={layanan?.attributes?.status}
                            setDateValue={setDateValue}
                          />
                        ))}
                    </Stack>
                  </Fade>
                  {isEligible('update_feedback') && (
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                    >
                      {isSaved ? 'Update Data' : 'Simpan Data'}
                    </LoadingButton>
                  )}

                  <>
                    {![
                      'tte',
                      'splp',
                      'aduan',
                      'videotron',
                      'cloud',
                      'backup',
                      'vpn',
                      'email'
                    ].includes(selectedRequirement.attributes.question_id) && (
                      <>
                        {isSaved &&
                          !isSigned &&
                          selectedRequirement.attributes.question_id !== 'feedback' && (
                            <Stack direction="column">
                              {/* <FormControl>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <FormLabel>Pilih mode tanda tangan</FormLabel>
                                  <Tooltip
                                    title="Silakan tandatangani untuk membuat dokumen feedback/jawaban."
                                    arrow
                                  >
                                    <Icon
                                      color={theme.palette.grey[500]}
                                      icon={questionMarkCircleFill}
                                    />
                                  </Tooltip>
                                </Stack>

                                <RadioGroup
                                  sx={{ padding: 1 }}
                                  aria-labelledby="Pilih mode tanda tangan"
                                  value={modeTandaTangan}
                                  name="modeTandaTangan"
                                  onChange={(event) => {
                                    setModeTandaTangan(event.target.value);
                                  }}
                                >
                                  <FormControlLabel
                                    value="tte"
                                    control={<Radio />}
                                    label="Tanda Tangan Elektronik (TTE) - Direkomendasikan"
                                  />
                                  <FormControlLabel
                                    value="manual"
                                    control={<Radio />}
                                    label="Tanda Tangan Manual"
                                  />
                                </RadioGroup>
                              </FormControl> */}
                              <Divider sx={{ marginBottom: 3 }} />
                              <LoadingButton
                                onClick={previewDocument}
                                fullWidth
                                size="large"
                                variant="contained"
                              >
                                Submit Tanda Tangan
                              </LoadingButton>
                            </Stack>
                          )}
                      </>
                    )}
                    {isSigned && (
                      <>
                        <Stack direction="row" pl={2} spacing={2} alignItems="center">
                          <Icon
                            icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                            width={32}
                            height={32}
                            color={isSigned ? theme.palette.primary.main : theme.palette.error.main}
                          />
                          <Typography variant="body2">
                            {isSigned ? 'Sudah ditandatangani' : 'Belum ditandatangani'}
                          </Typography>
                          <Button onClick={openDocument} disabled={!isSigned} variant="outlined">
                            Lihat dokumen
                          </Button>
                        </Stack>
                      </>
                    )}
                  </>
                </Stack>
                <Box sx={{ flex: 4 }}>
                  <AddAttachment
                    idRequirement={layanan?.attributes?.id_feedback}
                    user={user}
                    type="feedback"
                  />
                  <Stack direction="column" spacing={2}>
                    <Typography variant="h5">Informasi</Typography>
                    {informations.map((item, index) => (
                      <Box key={item.id}>
                        <Stack direction="row" spacing={2}>
                          <Box>
                            <Typography variant="body2">{index + 1}.</Typography>
                          </Box>
                          <Box sx={{ flex: 10 }}>
                            <Typography variant="body2">{item.label}</Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Form>
          </FormikProvider>
        </Stack>
      )}

      {/* FEEDBACK RESULT */}
      {isEligible('feedback_result') && (
        <Stack direction="row">
          <Box sx={{ flex: 6 }}>
            {layanan?.meta?.feedback?.metadata?.answers ? (
              <Stack direction="column" spacing={2}>
                {isSigned &&
                  questions.map((item, index) => (
                    <Stack direction="column" key={index}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {item.label}
                      </Typography>
                      {Array.isArray(values[item.id]) ? (
                        <Stack direction="column" spacing={1}>
                          {values[item.id].map((answerItem, answerIndex) => (
                            <Stack direction="row" spacing={1}>
                              <Typography variant="body2">{answerIndex + 1}. </Typography>
                              <Typography style={{ whiteSpace: 'pre-line' }} variant="body2">
                                {answerItem.value}{' '}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography style={{ whiteSpace: 'pre-line' }} variant="body2">
                          {values[item.id]}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                {!isSigned &&
                  [
                    'feedback',
                    'splp',
                    'aduan',
                    'videotron',
                    'cloud',
                    'backup',
                    'vpn',
                    'email'
                  ].includes(selectedRequirement.attributes.question_id) &&
                  questions.map((item, index) => (
                    <Stack direction="column" key={index}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {item.label}
                      </Typography>
                      {Array.isArray(values[item.id]) ? (
                        <Stack direction="column" spacing={1}>
                          {values[item.id].map((answerItem, answerIndex) => (
                            <Stack direction="row" spacing={1}>
                              <Typography variant="body2">{answerIndex + 1}. </Typography>
                              <Typography style={{ whiteSpace: 'pre-line' }} variant="body2">
                                {answerItem.value}{' '}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography style={{ whiteSpace: 'pre-line' }} variant="body2">
                          {values[item.id]}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                {/* THIS IS REQUESTOR STATUS PERMOHONAN VIEW */}
                <>
                  {![
                    'feedback',
                    'tte',
                    'splp',
                    'aduan',
                    'videotron',
                    'cloud',
                    'backup',
                    'vpn',
                    'email'
                  ].includes(selectedRequirement.attributes.question_id) && ( // feedback = tindak lanjut, rekomendasi = jawaban rekomendasi}
                    <Stack direction="row" pl={2} spacing={2} alignItems="center">
                      <Icon
                        icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                        width={32}
                        height={32}
                        color={isSigned ? theme.palette.primary.main : theme.palette.error.main}
                      />
                      <Typography variant="body2">
                        {isSigned ? 'Sudah ditandatangani' : 'Belum ditandatangani'}
                      </Typography>
                      <Button onClick={openDocument} disabled={!isSigned} variant="outlined">
                        Lihat dokumen
                      </Button>
                    </Stack>
                  )}
                </>
              </Stack>
            ) : (
              <Stack>
                <Typography variant="inherit">Maaf belum ada hasil</Typography>
              </Stack>
            )}
          </Box>

          <Box sx={{ flex: 6 }}>
            <Stack direction="column" spacing={2}>
              <Typography variant="h5">Informasi</Typography>
              {informations.map((item, index) => (
                <Box key={item.id}>
                  <Stack direction="row" spacing={2}>
                    <Box>
                      <Typography variant="body2">{index + 1}.</Typography>
                    </Box>
                    <Box sx={{ flex: 10 }}>
                      <Typography variant="body2">{item.label}</Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
            <Stack direction="column" mt={2}>
              <AddAttachment
                idRequirement={layanan?.attributes?.id_feedback}
                user={user}
                type="feedback"
              />
            </Stack>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

export default ServicesDetailFeedbackQuestions;
